import { redactLinkTokens, type ClientErrorReport } from '@pingo/shared';
import { API_BASE_URL } from './api';

/**
 * Client error reporting (CLAUDE.md §15 #278): render crashes, unhandled errors, offline-queue
 * (IndexedDB) and service-worker failures on caddy devices are beaconed to
 * POST /api/v1/telemetry/client-errors; the api logs them and forwards them to Sentry with its own
 * SENTRY_DSN, so the web build needs no DSN and no extra SDK.
 *
 * On by default in production builds; `VITE_ERROR_REPORTING=off` disables it, `=on` enables it in
 * dev. Reports never contain the query string (link tokens) or session tokens; at most
 * MAX_REPORTS per page load, identical messages at most once per DEDUPE_MS.
 */
export type ClientErrorKind = ClientErrorReport['kind'];

const MAX_REPORTS = 20;
const DEDUPE_MS = 60_000;
const ENDPOINT = '/api/v1/telemetry/client-errors';

let sent = 0;
const recent = new Map<string, number>();

export function reportingEnabled(): boolean {
  const flag = import.meta.env.VITE_ERROR_REPORTING;
  if (flag === 'off') return false;
  if (flag === 'on') return true;
  return import.meta.env.PROD;
}

/**
 * Remove query strings, fragments and link-token path segments (/t/:token, /c/:token, …) from text
 * that leaves the device; the api redacts again before logging (CLAUDE.md §15 #329).
 */
export function scrub(text: string): string {
  return redactLinkTokens(
    text.replace(/([?#][^\s'")]*)/g, '').replace(/Bearer\s+\S+/gi, 'Bearer ***'),
  );
}

/** Build the report body (pure; exported for tests). */
export function buildReport(
  kind: ClientErrorKind,
  error: unknown,
  context?: Record<string, string>,
): ClientErrorReport {
  const err = error instanceof Error ? error : null;
  const raw = err ? `${err.name}: ${err.message}` : String(error);
  return {
    kind,
    message: scrub(raw).slice(0, 2000) || kind,
    stack: err?.stack ? scrub(err.stack).slice(0, 8000) : undefined,
    path: typeof location !== 'undefined' ? scrub(location.pathname).slice(0, 300) : undefined,
    release: import.meta.env.VITE_APP_VERSION,
    userAgent: typeof navigator !== 'undefined' ? navigator.userAgent.slice(0, 400) : undefined,
    context,
  };
}

/** Fire-and-forget report; never throws, never blocks the UI. */
export function reportClientError(
  kind: ClientErrorKind,
  error: unknown,
  context?: Record<string, string>,
): void {
  try {
    if (!reportingEnabled() || sent >= MAX_REPORTS) return;
    const report = buildReport(kind, error, context);
    const key = `${kind}:${report.message}`;
    const now = Date.now();
    if ((recent.get(key) ?? 0) > now - DEDUPE_MS) return;
    recent.set(key, now);
    sent += 1;
    const body = JSON.stringify(report);
    const url = `${API_BASE_URL}${ENDPOINT}`;
    // fetch keepalive (not sendBeacon): a JSON beacon is not CORS-safelisted, and in dev the api
    // is a different origin; keepalive still delivers the report while the page unloads
    void fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body,
      keepalive: true,
    }).catch(() => undefined);
  } catch {
    /* reporting must never break the app */
  }
}

/** `promise.catch(reportAndIgnore('queue', { stage }))` for best-effort storage writes. */
export function reportAndIgnore(kind: ClientErrorKind, context?: Record<string, string>) {
  return (err: unknown): undefined => {
    reportClientError(kind, err, context);
    return undefined;
  };
}

/** window.onerror / unhandledrejection → reports (called once from main.tsx). */
export function installGlobalErrorReporting(): void {
  if (typeof window === 'undefined') return;
  window.addEventListener('error', (e) => reportClientError('error', e.error ?? e.message));
  window.addEventListener('unhandledrejection', (e) =>
    reportClientError('unhandledrejection', e.reason),
  );
}

/** Test hook: reset the per-page counters. */
export function resetTelemetryForTests(): void {
  sent = 0;
  recent.clear();
}
